// Support-Posteingang im Inhaberbereich.
//
// Ungelesene stehen oben, dann nach letzter Bewegung. Neben dem Verlauf steht,
// wer fragt — Mandant und Tarif —, weil die Antwort davon oft abhängt.
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { useCallback, useEffect, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { datumZeit } from '../../../gemeinsam/lib/daten';
import type { Anhang } from '../../firma/lib/dateien';
import { Abzeichen, Auswahl, Hinweis, Karte, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';
import { AnhangWahl, SupportVerlauf, type SupportNachricht } from './SupportVerlauf';
import MandantAnschreiben from '../../mandanten/komponenten/MandantAnschreiben';

type Status = 'offen' | 'in_arbeit' | 'wartet' | 'erledigt';
type Kurz = {
  id: string; nummer: string; betreff: string; kategorie: string; status: Status;
  tenantName: string; erstelltVonName: string; ungelesen: boolean; updatedAt: string;
  letzteNachricht: { von: 'kunde' | 'support'; text: string; zeit: string } | null;
};
type Voll = Kurz & {
  nachrichten: SupportNachricht[];
  mandant: { id: string; name: string; plan: string; email: string } | null;
  erstelltVonMail: string;
};
type Zaehler = { ungelesen: number; offen: number; inArbeit: number; wartet: number; erledigt: number };

const STATUS_NAME: Record<Status, string> = { offen: 'Offen', in_arbeit: 'In Arbeit', wartet: 'Wartet auf Kunde', erledigt: 'Erledigt' };
const STATUS_FARBE: Record<Status, 'rot' | 'gelb' | 'blau' | 'gruen'> = { offen: 'rot', in_arbeit: 'gelb', wartet: 'blau', erledigt: 'gruen' };
const KATEGORIE_NAME: Record<string, string> = { frage: 'Frage', fehler: 'Fehler', wunsch: 'Wunsch', abrechnung: 'Abrechnung', sonstiges: 'Sonstiges' };

export default function SupportPosteingang({ beiZaehler }: { beiZaehler?: (n: number) => void }) {
  const [filter, setFilter] = useState<'aktiv' | Status | 'alle'>('aktiv');
  const [liste, setListe] = useState<Kurz[] | null>(null);
  const [zaehler, setZaehler] = useState<Zaehler | null>(null);
  const [offen, setOffen] = useState<Voll | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [schreibt, setSchreibt] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const d = await api<{ anfragen: Kurz[]; zaehler: Zaehler }>(
        `/support/owner/liste${filter === 'alle' ? '' : `?status=${filter}`}`);
      setListe(d.anfragen);
      setZaehler(d.zaehler);
      beiZaehler?.(d.zaehler.ungelesen);
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Fehler beim Laden.'); }
  }, [filter, beiZaehler]);

  useEffect(() => { void laden(); }, [laden]);

  async function oeffnen(id: string) {
    try {
      setOffen(await api<Voll>(`/support/owner/${id}`));
      void laden();
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Fehler beim Öffnen.'); }
  }

  if (offen) {
    return <Detail anfrage={offen} beiZurueck={() => { setOffen(null); void laden(); }} beiAktualisiert={setOffen} />;
  }

  const filterKnoepfe: Array<[typeof filter, string, number | undefined]> = [
    ['aktiv', 'Aktiv', zaehler ? zaehler.offen + zaehler.inArbeit + zaehler.wartet : undefined],
    ['offen', 'Offen', zaehler?.offen],
    ['in_arbeit', 'In Arbeit', zaehler?.inArbeit],
    ['wartet', 'Wartet auf Kunde', zaehler?.wartet],
    ['erledigt', 'Erledigt', zaehler?.erledigt],
    ['alle', 'Alle', undefined],
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
        {filterKnoepfe.map(([id, name, n]) => (
          <button key={id} onClick={() => setFilter(id)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${filter === id
                    ? 'bg-marke-600 text-white'
                    : 'border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
            {name}{n !== undefined && <span className="ms-1.5 opacity-70">{n}</span>}
          </button>
        ))}
        </div>
        <Knopf variante="primaer" onClick={() => setSchreibt(true)}>Mandanten anschreiben</Knopf>
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      <Karte>
        {liste === null ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : liste.length === 0 ? (
          <LeerZustand titel="Keine Anfragen" text={filter === 'aktiv' ? 'Alles beantwortet.' : undefined} />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {liste.map(a => (
              <li key={a.id}>
                <button onClick={() => oeffnen(a.id)}
                        className="flex w-full items-start gap-3 px-5 py-3.5 text-start hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${a.ungelesen ? 'bg-rose-600' : 'bg-transparent'}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className={`text-slate-900 dark:text-slate-100 ${a.ungelesen ? 'font-semibold' : 'font-medium'}`}>{a.betreff}</span>
                      <Abzeichen art={STATUS_FARBE[a.status]}>{STATUS_NAME[a.status]}</Abzeichen>
                    </span>
                    <span className="mt-0.5 block truncate text-sm text-slate-500 dark:text-slate-400">
                      {a.letzteNachricht?.von === 'support' ? 'Du: ' : ''}{a.letzteNachricht?.text}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-400">
                      <strong className="font-medium text-slate-600 dark:text-slate-300">{a.tenantName}</strong>
                      {' · '}{a.erstelltVonName} · {a.nummer} · {KATEGORIE_NAME[a.kategorie] ?? a.kategorie} · {datumZeit(a.updatedAt)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Karte>

      {schreibt && (
        <MandantAnschreiben
          beiAbbruch={() => setSchreibt(false)}
          beiFertig={async (text, anfrageId) => {
            setSchreibt(false);
            setMeldung(text);
            await laden();
            // Bei einem einzelnen Empfänger direkt in das Gespräch springen —
            // meistens will man gleich sehen, wie es beim Kunden aussieht.
            if (anfrageId) void oeffnen(anfrageId);
          }}
        />
      )}
    </div>
  );
}

function Detail({ anfrage, beiZurueck, beiAktualisiert }: {
  anfrage: Voll; beiZurueck: () => void; beiAktualisiert: (a: Voll) => void;
}) {
  const [text, setText] = useState('');
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const [neuerStatus, setNeuerStatus] = useState<Status>('wartet');
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function antworten(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setSendet(true); setFehler(null); setHinweis(null);
    try {
      const r = await api<{ anfrage: Voll; mailZugestellt: boolean }>(`/support/owner/${anfrage.id}/antwort`, {
        methode: 'POST', daten: { text, dateiIds: anhaenge.map(a => a.id), status: neuerStatus },
      });
      beiAktualisiert({ ...anfrage, ...r.anfrage, mandant: anfrage.mandant });
      setText(''); setAnhaenge([]);
      setHinweis(r.mailZugestellt
        ? 'Antwort gespeichert, der Kunde hat eine Mail bekommen.'
        : 'Antwort gespeichert. Mail konnte nicht zugestellt werden — der Kunde sieht die Antwort trotzdem in der App und in seiner Glocke.');
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Antwort fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  async function statusSetzen(status: Status) {
    try {
      await api(`/support/owner/${anfrage.id}/status`, { methode: 'PUT', daten: { status } });
      beiAktualisiert({ ...anfrage, status });
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Ging nicht.'); }
  }

  return (
    <div className="space-y-4">
      <button onClick={beiZurueck} className="text-sm text-marke-700 hover:underline dark:text-marke-300">← Alle Anfragen</button>

      <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
        <Karte>
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{anfrage.betreff}</h2>
              <Abzeichen art={STATUS_FARBE[anfrage.status]}>{STATUS_NAME[anfrage.status]}</Abzeichen>
            </div>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {anfrage.nummer} · {KATEGORIE_NAME[anfrage.kategorie] ?? anfrage.kategorie}
            </p>
          </div>

          <div className="p-5">
            <SupportVerlauf nachrichten={anfrage.nachrichten} sicht="support"
                            anhangPfad={id => apiUrl(`/api/support/owner/${anfrage.id}/datei/${id}`)}
                            beschriftung={{ eigene: 'Du', fremde: 'Support' }} />
          </div>

          <form onSubmit={antworten} className="space-y-3 border-t border-slate-200 p-5 dark:border-slate-800">
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
            <Erfolgsmeldung text={hinweis} />
            <textarea value={text} onChange={e => setText(e.target.value)} rows={4} maxLength={10000}
                      placeholder="Antwort an den Kunden …" autoFocus
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
            <div className="flex flex-wrap items-end justify-between gap-3">
              <AnhangWahl anhaenge={anhaenge} setAnhaenge={setAnhaenge} beschriftung="Anhängen" beiFehler={setFehler} />
              <div className="flex items-end gap-2">
                <div className="w-44">
                  <Auswahl label="Danach" value={neuerStatus} onChange={e => setNeuerStatus(e.target.value as Status)}>
                    <option value="wartet">Wartet auf Kunde</option>
                    <option value="in_arbeit">In Arbeit</option>
                    <option value="erledigt">Erledigt</option>
                  </Auswahl>
                </div>
                <Knopf type="submit" variante="primaer" laedt={sendet} disabled={!text.trim()}>Antworten</Knopf>
              </div>
            </div>
          </form>
        </Karte>

        <div className="space-y-4">
          <Karte klasse="p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Kunde</p>
            <p className="mt-1.5 font-medium text-slate-900 dark:text-slate-100">{anfrage.mandant?.name ?? anfrage.tenantName}</p>
            {anfrage.mandant && <p className="text-sm text-slate-500 dark:text-slate-400">Tarif {anfrage.mandant.plan}</p>}
            <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{anfrage.erstelltVonName}</p>
            {anfrage.erstelltVonMail && (
              <a href={`mailto:${anfrage.erstelltVonMail}`} className="text-sm text-marke-700 hover:underline dark:text-marke-300">
                {anfrage.erstelltVonMail}
              </a>
            )}
          </Karte>
          <Karte klasse="p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Status setzen</p>
            <div className="flex flex-col gap-1.5">
              {(['offen', 'in_arbeit', 'wartet', 'erledigt'] as Status[]).map(s => (
                <Knopf key={s} className="!justify-start !text-xs" disabled={anfrage.status === s}
                       variante={anfrage.status === s ? 'primaer' : 'sekundaer'} onClick={() => statusSetzen(s)}>
                  {STATUS_NAME[s]}
                </Knopf>
              ))}
            </div>
          </Karte>
        </div>
      </div>
    </div>
  );
}
